import type { PatchedLocationDescriptor } from 'js/lib/connectToRouter';

import type { MergedDegreeDescriptionPageFields } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { ProgramAdmissionDeadlines } from 'bundles/degree-description/types';
import { DEADLINE_TYPES, getDegreeDeadlineType } from 'bundles/degree-description/utils/ctaModuleDeadline';
import { showCtaModuleDeadline } from 'bundles/degree-description/utils/experiments';
import adsTracker from 'bundles/event-tracking/lib';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { isDestinationDegree, isPerformancePathwayDegree } from 'bundles/expression-of-interest/utils/util';

import _t from 'i18n!nls/degree-description';

export type EoiTextType = {
  applyButtonText?: string | null;
  emailMeButtonText?: string | null;
  emailMeConfirmationText?: string | null;
  programInterestText?: string | null;
  inlineTitle?: string | null;
  inlineDescription?: string | null;
};

export const deriveActionBarData = (pageContent: MergedDegreeDescriptionPageFields) => {
  const { programName, partner } = pageContent;

  return {
    isFixed: false,
    partnerName: partner?.partnerName,
    programName,
    partnerMonotoneLogo: partner?.partnerMonotoneLogo?.url,
  };
};

export const deriveEoiText = (pageContent: MergedDegreeDescriptionPageFields): EoiTextType => {
  const { expressionOfInterest, slug } = pageContent;

  // Required EOI is for Destination Degrees only
  const requiredEoiEnabled = isDestinationDegree(slug || '');
  const performancePathwayDegree = isPerformancePathwayDegree(slug || '');

  if (requiredEoiEnabled) {
    let emailMeButtonText;
    let programInterestDescription;

    if (!expressionOfInterest?.applyButtonUrl) {
      emailMeButtonText = _t('Request info');
      programInterestDescription = _t('Request more info about this program');
    } else if (performancePathwayDegree) {
      emailMeButtonText = _t('Enroll now');
      programInterestDescription = _t('Request more info and start your first course');
    } else {
      emailMeButtonText = _t('View application now');
      programInterestDescription = _t('Request more info and start your application');
    }

    const programInterestTitle = _t('Learn more about this program');
    const programInterestText = `## ${programInterestTitle}\n${programInterestDescription}`;

    return {
      applyButtonText: expressionOfInterest?.applyButtonText,
      emailMeButtonText: emailMeButtonText ?? expressionOfInterest?.emailMeButtonText,
      programInterestText: programInterestText ?? expressionOfInterest?.programInterestText,
      emailMeConfirmationText: expressionOfInterest?.emailMeConfirmationText,
      inlineTitle: expressionOfInterest?.inlineTitle,
      inlineDescription: expressionOfInterest?.inlineDescription,
    };
  }

  return {
    applyButtonText: expressionOfInterest?.applyButtonText,
    emailMeButtonText: expressionOfInterest?.emailMeButtonText,
    programInterestText: expressionOfInterest?.programInterestText,
    emailMeConfirmationText: expressionOfInterest?.emailMeConfirmationText,
    inlineTitle: expressionOfInterest?.inlineTitle,
    inlineDescription: expressionOfInterest?.inlineDescription,
  };
};

export const deriveEoiButtons = (
  id: string,
  pageContent: NonNullable<MergedDegreeDescriptionPageFields>,
  handleCtaClick: () => void,
  programAdmissionDeadlinesData?: ProgramAdmissionDeadlines,
  handleOpenEOIModalForApplication?: () => void
): EoiButtonsType => {
  const { slug, expressionOfInterest, programName } = pageContent;

  // Required EOI Experiment
  const requiredEoiEnabled = isDestinationDegree(slug || '');

  // CTA Module Deadline Experiment
  const showCtaModuleDeadlineCopy = (slug || '') in showCtaModuleDeadline();
  const deadlineType = getDegreeDeadlineType(slug || '', programAdmissionDeadlinesData || []);
  const applicationPeriodClosed =
    showCtaModuleDeadlineCopy &&
    (deadlineType === DEADLINE_TYPES.upcomingAdmissions || deadlineType === DEADLINE_TYPES.noUpcomingAdmissions);

  const trackDegreeApplyNow = () => {
    adsTracker.trackDegreeApplyNow(slug, id, programName, 'Degree');
  };

  const trackRequestInfo = () => {
    adsTracker.trackEmailMeInfo(slug, id, programName, 'Degree');
  };

  const deriveDefaultApplyButtonData = (applicationUrl: string) => {
    if (!applicationUrl) {
      return undefined;
    }

    let buttonOptions;

    if (handleOpenEOIModalForApplication) {
      buttonOptions = {
        onClick: handleOpenEOIModalForApplication,
      };
    } else {
      buttonOptions = {
        tag: 'a' as const,
        htmlAttributes: {
          href: applicationUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      };
    }

    return {
      actionType: ACTION_TYPES.apply,
      isExternalLink: true,
      doAction: trackDegreeApplyNow,
      buttonOptions,
    };
  };

  const getApplyButtonData = () => {
    const applicationUrl = expressionOfInterest?.applyButtonUrl || expressionOfInterest?.hostedApplicationUrl;
    if (applicationUrl) {
      return deriveDefaultApplyButtonData(applicationUrl);
    }

    return null;
  };

  const getRequestInfoData = () => {
    return {
      actionType: ACTION_TYPES.emailMeInfo,
      doAction: () => trackRequestInfo(),
      buttonOptions: {
        onClick: handleCtaClick,
        ...(requiredEoiEnabled || applicationPeriodClosed ? { type: 'primary' } : { type: 'secondary' }),
      },
    };
  };

  const applyButtonData = getApplyButtonData();
  const requestInfoData = {
    ...getRequestInfoData(),
    ...(!applyButtonData && { buttonOptions: { onClick: handleCtaClick, type: 'primary' } }),
  };

  const eoiButtons = [];

  if (applyButtonData) {
    eoiButtons.push(applyButtonData);
  }

  eoiButtons.push(requestInfoData);

  return eoiButtons;
};

export const routerLocationHasRequestInfoHash = (location: PatchedLocationDescriptor) =>
  location?.hash === '#requestinfo';

export const routerLocationHasEnrollApplyHash = (location: PatchedLocationDescriptor) =>
  location?.hash === '#enrollOrApply';
